import React, { useState } from 'react';
import { Bell, Search, User, Palette, Menu } from 'lucide-react'; // Menu icon imported
import { useAdminTheme, ThemeMode } from '../context/AdminThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

// Added Props Interface
interface AdminNavbarProps {
    onMenuClick: () => void;
}

const AdminNavbar: React.FC<AdminNavbarProps> = ({ onMenuClick }) => {
  const { theme, setTheme, colors } = useAdminTheme();
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);

  const themeOptions: { id: ThemeMode; label: string; color: string }[] = [
    { id: 'red', label: 'PIT Red', color: 'bg-red-600' },
    { id: 'dark', label: 'Dark Mode', color: 'bg-gray-900' },
    { id: 'blue', label: 'Ocean Blue', color: 'bg-blue-600' },
    { id: 'purple', label: 'Royal Purple', color: 'bg-purple-600' },
  ];

  return (
    <header className={`h-20 ${colors.navbarBg} ${colors.navbarText} border-b border-gray-200/50 flex items-center justify-between px-4 md:px-8 fixed top-0 right-0 left-0 lg:left-64 z-30 shadow-sm transition-all duration-500`}>
      
      {/* Left Side: Mobile Menu Button & Search */}
      <div className="flex items-center gap-4">
          
          {/* Mobile Menu Button */}
          <button 
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
          >
            <Menu size={24} />
          </button>

          {/* Search Bar (Hidden on very small screens) */}
          <div className="relative w-full max-w-md hidden sm:block">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input 
                type="text" 
                placeholder="Search anything..." 
                className={`w-64 md:w-96 pl-10 pr-4 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all text-sm
                ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:ring-gray-500' : 'bg-gray-100 focus:ring-red-500'}
                `}
            />
          </div>
      </div>

      {/* Right Actions */}
      <div className="flex items-center space-x-3 md:space-x-5">
         
         {/* THEME SWITCHER */}
         <div className="relative">
            <button 
                onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
                className={`p-2.5 rounded-full transition-all duration-300 hover:scale-110 shadow-sm
                    ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-600'}
                `}
                title="Change Theme"
            >
                <Palette size={20} />
            </button>

            <AnimatePresence>
                {isThemeMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className={`absolute right-0 mt-3 w-48 rounded-xl shadow-2xl border overflow-hidden py-2 z-50
                            ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}
                        `}
                    >
                        <p className={`px-4 py-2 text-xs font-bold uppercase tracking-wider opacity-50 ${theme==='dark'?'text-white':'text-gray-500'}`}>Select Theme</p>
                        {themeOptions.map((option) => (
                            <button
                                key={option.id}
                                onClick={() => { setTheme(option.id); setIsThemeMenuOpen(false); }}
                                className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors
                                    ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'}
                                    ${theme === option.id ? (theme === 'dark' ? 'bg-gray-700' : 'bg-red-50 text-red-600') : ''}
                                `}
                            >
                                <div className={`w-4 h-4 rounded-full ${option.color} ring-2 ring-offset-1 ring-offset-transparent ring-gray-300`}></div>
                                <span className="text-sm font-medium">{option.label}</span>
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
         </div>

         {/* Notification Bell */}
         <div className="relative cursor-pointer group">
             <div className={`p-2.5 rounded-full transition-colors ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <Bell size={20} />
             </div>
             <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center animate-bounce">
                3
             </span>
         </div>

         {/* Profile */}
         <div className={`flex items-center gap-3 pl-4 border-l ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="text-right hidden md:block">
                <p className="text-sm font-bold">Admin User</p>
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Super Admin</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-red-500 to-red-700 p-0.5 shadow-lg cursor-pointer hover:scale-105 transition-transform">
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                     <User size={20} className="text-red-600" />
                </div>
            </div>
         </div>
      </div>
    </header>
  );
};

export default AdminNavbar;